/**
 * DI-02 — reads the speaker notes text out of a single-slide .pptx package.
 *
 * The PowerPoint JS API has no speaker-notes surface. `slide.exportAsBase64()`
 * (PowerPointApi 1.8) returns the slide as its own .pptx, and that package
 * carries the slide's notes page as a linked part:
 *
 *   ppt/slides/slideN.xml
 *   ppt/slides/_rels/slideN.xml.rels   ── Type …/notesSlide ──►  ppt/notesSlides/notesSlideM.xml
 *
 * The notes body is the shape whose placeholder is `type="body"`. The other
 * shapes on a notes page (slide image, slide number, header/footer, date)
 * are layout furniture and are ignored. Same relationship resolution as
 * Deckorate's SpeakerNotes feature (deckorate/utils/ooxmlRelationships).
 */

import { strFromU8, unzipSync } from "fflate";
import { resolveRelationshipTarget } from "../../../../deckorate/utils/ooxmlRelationships";

const NS_P = "http://schemas.openxmlformats.org/presentationml/2006/main";
const NS_A = "http://schemas.openxmlformats.org/drawingml/2006/main";
const NOTES_SLIDE_REL = "/notesSlide";
const SLIDE_PART = /^ppt\/slides\/slide\d+\.xml$/;

/** Joins a text body's paragraphs with newlines, keeping line breaks inside a paragraph. */
function textBodyToString(txBody: Element): string {
  const paragraphs = Array.from(txBody.getElementsByTagNameNS(NS_A, "p"));
  return paragraphs
    .map((p) => {
      let line = "";
      for (const node of Array.from(p.getElementsByTagNameNS(NS_A, "*"))) {
        if (node.localName === "t") line += node.textContent ?? "";
        else if (node.localName === "br") line += "\n";
      }
      return line;
    })
    .join("\n");
}

/** The notes body text of a notesSlide part, or "" when it has none. */
export function notesTextFromNotesXml(notesXml: string): string {
  const doc = new DOMParser().parseFromString(notesXml, "text/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) return "";

  for (const sp of Array.from(doc.getElementsByTagNameNS(NS_P, "sp"))) {
    const ph = sp.getElementsByTagNameNS(NS_P, "ph")[0];
    if (ph?.getAttribute("type") !== "body") continue;
    const txBody = sp.getElementsByTagNameNS(NS_P, "txBody")[0];
    return txBody ? textBodyToString(txBody).trim() : "";
  }
  return "";
}

/**
 * Speaker notes of the (only) slide in an exported single-slide package.
 * Returns "" when the slide has no notes page or the notes are blank.
 * Throws when the bytes are not a readable .pptx.
 */
export function notesTextFromSlidePackage(pptx: Uint8Array): string {
  const files = unzipSync(pptx, {
    filter: (f) => SLIDE_PART.test(f.name) || f.name.startsWith("ppt/slides/_rels/") || f.name.startsWith("ppt/notesSlides/"),
  });

  const slidePath = Object.keys(files).find((name) => SLIDE_PART.test(name));
  if (!slidePath) throw new Error("exported package has no slide part");

  const fileName = slidePath.slice("ppt/slides/".length);
  const rels = files[`ppt/slides/_rels/${fileName}.rels`];
  const notesPath = resolveRelationshipTarget(rels ? strFromU8(rels) : null, NOTES_SLIDE_REL, "ppt/slides");
  if (!notesPath) return "";

  const notes = files[notesPath];
  return notes ? notesTextFromNotesXml(strFromU8(notes)) : "";
}
